import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';

import { auth, db } from "../../../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, collection, getDoc, getDocs, setDoc, updateDoc, writeBatch, deleteDoc, addDoc } from 'firebase/firestore';

import SEO from '../../SEO';
import Popup from "../../mobile/Popup";
import Loading from "../../Loading";
import MyPageMenu from './MyPageMenu';
import ArchiveDetailTracks from '../ArchiveDetailTracks'; // 새로 추가
import { saveUserLog } from '../LogandUtils'; // 경로에 맞게 수정

import styleClasses from '../css/mobile/MyPage.module.scss';

import imageCompression from 'browser-image-compression';

function ArchiveDetail() {
  const { attribute, albumID }       = useParams();
  // albumID가 "new"면 새 앨범 추가 모드로 간주
  const isNew = albumID === "new";

  const albumNameRef  = useRef();
  const isSubmitting  = useRef(false);

  const [openMenu, setOpenMenu]                     = useState(false);
  const [isLoading, setIsLoading]                   = useState(true);
  const [isImageLoading, setIsImageLoading]         = useState(false); // 앨범 이미지
  const [tracks, setTracks]                         = useState([{ title: '', roles: '', url: '' }]);
  const [date, setDate]                             = useState('');
  const [artist, setArtist]                         = useState('');
  const [description, setDescription]               = useState('');
  const [image, setImage]                           = useState('');   // 이미지 표시용
  const [cover, setCover]                           = useState(null); // 폼 제출용
  const [uid, setUid]                               = useState(null); // 🔹 UID 상태 추가
  const [albumName, setAlbumName]                   = useState('');
  const [albumTypeSelected, setAlbumTypeSelected]   = useState('싱글'); // 기본값은 원하는 타입으로

  const [albumNameError, setAlbumNameError]         = useState('');
  const [albumNameErrorText, setAlbumNameErrorText] = useState('');
  const [buttonLoading, setButtonLoading]           = useState(false)

  const [openPopup, setOpenPopup]                   = useState(false);
  const [popupTitle, setPopupTitle]                 = useState('');
  const [popupButtonText, setPopupButtonText]       = useState('');
  const [customFunction, setCustomFunction]         = useState('');
  //const albumType = attribute === 'own-album' ? 'ownAlbums' : attribute === 'collaboration-albums' ? 'collaboAlbums' : attribute === 'unreleased' ? 'unreleasedAlbums' : '';

  const albumType = attribute === 'released' ? 'released' : 'unreleased';

  // 🔹 메뉴 열기
  const toogleMenu = useCallback(
    (event) => {
      const menuIcon = event.target.closest(`.${styleClasses.menuIcon}`);
      const menuBox = event.target.closest(`.${styleClasses.leftContent}`);

      if (openMenu) {
        if (!menuBox) setOpenMenu(false);
      } else {
        if (menuIcon || menuBox) setOpenMenu(true);
      }
    },
    [openMenu, setOpenMenu]
  );

  // 🔹 메뉴 열기 (스크린 전체 이벤트 추가)
  useEffect(() => {
    document.body.addEventListener("click", toogleMenu);

    return () => {
      document.body.removeEventListener("click", toogleMenu);
    };
  }, [toogleMenu]);

  // 🔹 현재 로그인된 사용자 UID 가져오기
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsImageLoading(true);
      const options = {
        maxWidthOrHeight: 500,
        useWebWorker: true,
        maxSizeMB: 0.5, // 원하는 최대 파일 사이즈(MB)
        fileType: 'image/jpeg',
        initialQuality: 0.8,
      };
      const compressedFile = await imageCompression(file, options);
      
      const storage = getStorage();
      const storageRef = ref(storage, `users/${uid}/archive/${file.name}`);
      const snapshot = await uploadBytes(storageRef, compressedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      setIsImageLoading(false);
      setImage(downloadURL);
      setCover(compressedFile);
    } catch (error) {
      setIsImageLoading(false);
      console.error("파일 업로드 오류:", error);
      
      setOpenPopup(true);
      setPopupTitle('파일 업로드 중 오류가 발생했습니다.');
      setPopupButtonText('');
      setCustomFunction('');
    }
  };  

  // 🔹 앨범 정보 가져오기 (useCallback)
  const fetchAlbums = useCallback(async () => {
    if (isNew) {
      setIsLoading(false);
      return;
    }
    try {
      const albumRef = doc(db, 'users_data', uid, albumType, albumID);
      const albumSnap = await getDoc(albumRef);
      
      if (albumSnap.empty) {
        console.error(`No albums found in ${albumType}`);
        setTracks([]);
        setIsLoading(false);
        return;
      }
  
      const albumData = albumSnap.data();
      // 만약 제목이 "[타입] 제목" 형식이면 파싱해서 분리
      const match = albumData.title && albumData.title.match(/^\[(.*?)\]\s*(.*)$/);
      if (match) {
        setAlbumTypeSelected(match[1]);
        setAlbumName(match[2]);
      } else {
        // 형식이 맞지 않으면 그냥 전체 제목을 넣고, 기본 타입 사용
        setAlbumName(albumData.title || '');
      }

      const tracksRef = collection(db, 'users_data', uid, albumType, albumID, 'tracks');
      const tracksSnapshot = await getDocs(tracksRef);
  
      const fetchedTracks = tracksSnapshot.docs.map((doc, index) => ({
        id: (index + 1).toString().padStart(2, '0'),
        ...doc.data(),
      }));      
  
      setImage(albumData.cover || ''); // ✅ `undefined` 방지
      //setTitle(albumData.title || '');
      setDate(formatDate(albumData.year));
      setArtist(albumData.artist || '');
      setDescription(albumData.description || '');
      setTracks(fetchedTracks);
      setIsLoading(false);
    } catch (error) {      
      setOpenPopup(true);
      setPopupTitle('앨범을 가져오는데 실패했습니다.');
      setPopupButtonText('');
      setCustomFunction('');
      console.error("Error fetching albums:", error);
    }
  }, [uid, albumID, albumType, isNew]);
  
  // 🔹 앨범 정보 가져오기 (useEffect)
  useEffect(() => {
    if (uid) {
      fetchAlbums();
    }
  }, [fetchAlbums, uid]);

  // 🔹 트랙 입력
  const handleTrackChange = (index, field, value) => {
    setTracks((prevTracks) =>
      prevTracks.map((track, i) =>
        i === index ? { ...track, [field]: value } : track
      )
    );
  };

  // 🔹 트랙 추가
  const addTrack = () => {
    setTracks((prevTracks) => [
      ...prevTracks,
      { title: '', roles: '', url: '', id: Date.now() }
    ]);
  };
  
  // 🔹 트랙 삭제
  const removeTrack = (index) => {
    setTracks((prevTracks) => prevTracks.filter((_, i) => i !== index));
  };

  // 🔹 날짜 변환
  const formatDate = (dateString) => {
    if (dateString.includes('.')) {
      const [year, month, day] = dateString.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // 이미 "YYYY-MM-DD" 형식인 경우 그대로 반환
    return dateString;
  };  

  // 🔹 입력값 변경 핸들러
  const handleChange = (e) => {
    if (e.target.id === `albumName${ albumID }`) {
      setAlbumNameError('');
      setAlbumNameErrorText('');
    }
  };

  // 🔹 추가 및 수정
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    let emptyTrack  = false;
    let trackIndex  = 0;
    if (tracks.length === 0) {
      emptyTrack = true;
    } else {
      for (let i = 0; i < tracks.length ; i++) {
        if (tracks[i].title === '') {
          emptyTrack = true;
          trackIndex = i + 1;
          break;
        }
      }
    }

    if (!uid) {
      setOpenPopup(true);
      setPopupTitle('로그인이 필요합니다.');
      setPopupButtonText('');
      setCustomFunction('');
      return;
    } else if (cover === null && image === '') {
      setOpenPopup(true);
      setPopupTitle('앨범 이미지를 업로드하세요.');
      setPopupButtonText('');
      setCustomFunction('');
      return false;
    } else if (albumName === '') {
      setAlbumNameError('error');
      setAlbumNameErrorText('앨범명을 입력하세요.');
      albumNameRef.current.focus();
      return false;
    } else if (tracks.length === 0) {
      setOpenPopup(true);
      setPopupTitle('트랙을 입력하세요.');
      setPopupButtonText('');
      setCustomFunction('');
      return false;
    } else if (emptyTrack) {
      setOpenPopup(true);
      setPopupTitle(`${ trackIndex }번째 트랙의 타이틀을 입력하세요.`);
      setPopupButtonText('');
      setCustomFunction('');
      return false;
    }
    
    try {
      if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
      isSubmitting.current = true;      // 버튼 비활성화
      setButtonLoading(true);           // 버튼 로딩 표시

      const fullTitle = `[${albumTypeSelected}] ${albumName}`;
      let updatedTracks = tracks.map((track, index) => ({
        ...track,
        id: (index + 1).toString().padStart(2, '0'),
      }));

      if (isNew) {
        // 새로운 앨범 생성
        let docId = "MANUAL_" + Date.now();
        const albumRef = doc(db, 'users_data', uid, albumType, docId);
        await setDoc(albumRef, {
          title: fullTitle,
          year: date,
          artist: artist,
          description: description,
          cover: image,
        });
    
        // 트랙 정보 저장 (하위 컬렉션 'tracks')
        const batch = writeBatch(db);
        const tracksRef = collection(db, 'users_data', uid, albumType, docId, 'tracks');
        updatedTracks.forEach((track) => {
          const trackDocRef = doc(tracksRef, track.id);
          batch.set(trackDocRef, track);
        });
        await batch.commit();
      } else {
        // 1. 사용자 앨범 기본 정보 업데이트 (merge:true로 기존 필드 유지)
        const albumRef = doc(db, 'users_data', uid, albumType, albumID);
        await updateDoc(albumRef, {
          title: fullTitle,
          year: date,
          artist: artist,
          description: description,
          cover: image,
        });
    
        // 2. 트랙 정보 업데이트 (하위 컬렉션 'tracks')
        const batch = writeBatch(db);
        const tracksRef = collection(db, 'users_data', uid, albumType, albumID, 'tracks');
    
        // 기존 트랙 문서 모두 삭제
        const existingTracksSnapshot = await getDocs(tracksRef);
        existingTracksSnapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });
    
        // 새 트랙 문서 생성
        updatedTracks.forEach((track) => {
          const trackDocRef = doc(tracksRef, track.id);
          batch.set(trackDocRef, track);
        });
        await batch.commit();
    
        // 3. act 필드 확인: 이미 뮤지카드로 내보낸 경우 뮤지카드의 앨범 문서도 업데이트
        const albumSnap = await getDoc(albumRef);
        if (albumSnap.exists()) {
          const albumData = albumSnap.data();
          if (albumData.act) {
            // act 필드 예시: { apodteam: "ownAlbums" }
            for (const musicianCardId in albumData.act) {
              const exportType = albumData.act[musicianCardId]; // 예: "ownAlbums"
              // 업데이트할 뮤지카드 앨범 문서 경로: /musicians/{뮤지카드ID}/{exportType}/{albumID}
              const musicianAlbumRef = doc(db, "musicians", musicianCardId, exportType, albumID);
              await setDoc(musicianAlbumRef, { updatedAt: new Date() }, { merge: true });
              await setDoc(musicianAlbumRef, {
                title: fullTitle,
                year: date,
                artist: artist,
                description: description,
                cover: image,
              }, { merge: true });
    
              // 뮤지카드 앨범의 하위 'tracks' 업데이트
              const musicianTracksRef = collection(db, "musicians", musicianCardId, exportType, albumID, "tracks");
              const existingMusicianTracksSnapshot = await getDocs(musicianTracksRef);
              await Promise.all(existingMusicianTracksSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
              await Promise.all(
                updatedTracks.map((track) =>
                  addDoc(musicianTracksRef, track)
                )
              );

              const changedPaths = [musicianAlbumRef.path, musicianTracksRef.path];
              await saveUserLog('musicard_logs', musicianCardId, 'update', changedPaths, 'ArchiveDetail');
         
            }
          }
        }
      }
      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김
      
      setOpenPopup(true);
      setPopupTitle('앨범 정보가 성공적으로 업데이트되었습니다!');
      setPopupButtonText('');
      setCustomFunction('');

      if (isNew) {
        setTracks([{ title: '', roles: '', url: '' }]);
        setDate('');
        setArtist('');
        setDescription('');
        setImage('');
        setCover(null);
        setAlbumName('');
        setAlbumTypeSelected('싱글');
      }
    } catch (error) {
      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김
      
      setOpenPopup(true);
      setPopupTitle('앨범 업데이트 중 오류가 발생했습니다.');
      setPopupButtonText('');
      setCustomFunction('');
      console.error("앨범 업데이트 오류:", error);
    }
  };  

  return (
    <>
      <SEO title={`아카이브 ${ isNew ? '추가' : '수정' }`} />
      <div className={ styleClasses.myPageMobile }>
        <div className={ styleClasses.inner }>
          <div className={ styleClasses.title }>
           <h1>Archive { isNew ? "(새 앨범 추가)" : "" }</h1>
           <button className={ styleClasses.menuIcon }>
              <div className="commonIcon mobile">
                <i className={`${ openMenu ? 'upWhite' : 'downWhite' } w24`}></i>
              </div>
            </button>
          </div>
          <div className={ styleClasses.editContent }>
          <div className={`${ styleClasses.leftContent } ${ openMenu ? styleClasses.open : '' }`}>
              <MyPageMenu />
            </div>
            <div className={ styleClasses.rightContent }>
              <div className={ styleClasses.content }>
                <div className={ styleClasses.myArchive }>
                {  isImageLoading ? <Loading /> : null }
                { 
                  isLoading
                  ?
                  <Loading />
                  :
                  <div className={ styleClasses.albumDetail }>
                    <div className={ styleClasses.detailBox }>
                      <div className="commonForm">
                        {/* 앨범 이미지 */}
                        <div className="formRow">
                          <div className="labelBox">
                            <label htmlFor={`cover${ albumID }`}>앨범 이미지</label>
                          </div>
                          <div className={`fieldBox ${ styleClasses.uploadImage }`}>
                            <label htmlFor={`cover${ albumID }`}>
                              {
                                image
                                ?
                                (
                                  <>
                                    <img src={ image } alt='앨범 이미지' />
                                  </>
                                )
                                :
                                (
                                  <div className={ styleClasses.placeholder }>이미지 선택</div>
                                )
                              }
                              {
                                isImageLoading
                                ?
                                <span className={`${ styleClasses.imageLoading } w24`}>
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                </span>
                                :
                                <div className="commonIcon mobile">
                                  <i className='uploadWhite w16'></i>
                                </div>
                              }
                              <input type="file" id={`cover${ albumID }`} name='cover' onChange={ handleFileChange } />
                            </label>
                            {/* 이미지 URL 입력란 추가 */}
                            <div className={ styleClasses.underFieldBox }>
                              {/* <div className="labelBox">
                                <label htmlFor={`coverURL${ albumID }`} style={{ width: 'unset', height: 'unset' }}></label>
                              </div> */}
                              <div className="fieldBox">
                                <input type="text" id={`coverURL${ albumID }`} name="coverURL" placeholder="또는 이미지 URL 입력" value={ image } onChange={(e) => setImage(e.target.value)} />
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* 앨범 타입 */}
                        <div className="formRow">
                          <div className="labelBox">
                              <label>앨범 타입</label>
                          </div>
                          <div className="fieldBox checkBox">
                            {["싱글", "EP", "정규", "옴니버스", "OST"].map((type) => (
                              <div className="eachBox" key={ type }>
                                <input type="radio" name="albumType" value={type} checked={albumTypeSelected === type} onChange={(e) => setAlbumTypeSelected(e.target.value)} />
                                <label>{ type }</label>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* 앨범명 */}
                        <div className="formRow">
                          <div className="labelBox">
                            <label htmlFor={`title${ albumID }`}>앨범명</label>
                          </div>
                          <div className={`fieldBox ${ albumNameError }`}>
                            <input type="text" id={`albumName${ albumID }`} value={albumName} ref={ albumNameRef } onChange={(e) => { setAlbumName(e.target.value); handleChange(e); } } />
                            <div className="fieldError">
                              <p>{ albumNameErrorText }</p>
                            </div>
                          </div>
                        </div>
                        {/* 발매일 */}
                        <div className="formRow">
                          <div className="labelBox">
                            <label htmlFor={`date${ albumID }`}>발매일</label>
                          </div>
                          <div className="fieldBox">
                            <input type="date" id={`date${ albumID }`} name='date' value={ date } onChange={ (e) => setDate(e.target.value) } />
                          </div>
                        </div>
                        {/* 아티스트 */}
                        <div className="formRow">
                          <div className="labelBox">
                            <label htmlFor={`artist${ albumID }`}>아티스트</label>
                          </div>
                          <div className="fieldBox">
                            <input type="text" id={`artist${ albumID }`} name='artist' value={ artist } onChange={ (e) => setArtist(e.target.value) } />
                          </div>
                        </div>
                        {/* 설명 */}
                        <div className="formRow">
                          <div className="labelBox">
                            <label htmlFor={`description${ albumID }`}>설명</label>
                          </div>
                          <div className="fieldBox">
                            <textarea id={`description${ albumID }`} name='description' value={ description } onChange={ (e) => setDescription(e.target.value) }></textarea>
                          </div>
                        </div>
                        {/* 트랙 : ArchiveDetailTracks 컴포넌트로 트랙 목록 드래그앤드롭 처리 */}
                        <ArchiveDetailTracks 
                          tracks={tracks}
                          setTracks={setTracks}
                          styleClasses={styleClasses}
                          handleTrackChange={handleTrackChange}
                          removeTrack={removeTrack}
                          addTrack={addTrack}
                          mobile={ true }
                        />
                      </div>
                    </div>
                    <div className={ styleClasses.buttonWrap }>
                      <div className={ styleClasses.topButtons }>
                        <div className="commonButton fullwidth">
                          <Link to={`/my-page/archive/${ attribute }/`} className='secondary'>목록</Link>
                        </div>
                        <div className="commonButton fullwidth">
                          <button className='black' onClick={ handleSubmit }>
                            저장
                            <div className={`buttonLoading ${ buttonLoading ? 'active' : undefined }`}>
                              <div></div>
                              <div></div>
                              <div></div>
                            </div>
                          </button>
                        </div>
                        <div className={`commonIcon mobile ${ styleClasses.deleteButton }`}>
                          <button>
                            <i className='trashWhite w24'></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Popup
        open={ openPopup }
        setOpenPopup={ setOpenPopup }
        title={ popupTitle }
        buttonText={ popupButtonText }
        buttonFunction={ customFunction }
      />
    </>
  );
}

export default ArchiveDetail;
